/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import settlementsMainPageHeadersConfiguration from './settlements-headers-configuration';
import {SettlementsApi} from '../api/settlements.api';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {
    forkJoin,
    Observable,
    of,
    ReplaySubject,
    Subject,
    Subscription,
    timer
} from 'rxjs';
import {TableModel} from '../../shared/table/models/table.model';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {SettlementsMainPageStateModel} from './settlements-main-page-state.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {TableSortedByModel} from '../../shared/table/models/table-sorted-by.model';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {SettlementSearchCriteriaService} from '../services/settlement-search-criteria.service';
import {DatepickerHelperService} from '../../shared/utils/datepicker-helper/datepicker-helper.service';
import {SettlementColumnEnum} from '../models/settlement-column.enum';
import {SettlementQueryContract} from '../api/contracts/settlement-query.contract';
import {
    finalize,
    take
} from 'rxjs/operators';
import {CodeFilterComponent} from '../../shared/filter/code-filter/code-filter.component';
import {CodeFilterModel} from '../../shared/filter/code-filter/code-filter.model';
import {CodeFilterStateModel} from '../../shared/filter/code-filter/code-filter-state.model';
import {AbstractFilterStateModel} from '../../shared/filter/abstract-filter-state.model';
import {InputAndCodeFilterComponent} from '../../shared/filter/input-and-code-filter/input-and-code-filter.component';
import {DateFilterComponent} from '../../shared/filter/date-filter/date-filter.component';
import {NumberFilterComponent} from '../../shared/filter/number-filter/number-filter.component';
import {SettlementSummaryModel} from '../models/settlements-sumary-model';
import {WithPagination} from '../../shared/pagination/with-pagination';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {SortDirection} from '../api/contracts/sort-direction.enum';
import {InputAndCodeFilterStateModel} from '../../shared/filter/input-and-code-filter/input-and-code-filter-state.model';
import {DateFilterStateModel} from '../../shared/filter/date-filter/date-filter-state.model';
import {NumberFilterStateModel} from '../../shared/filter/number-filter/number-filter-state.model';
import {AppService} from '../../app.service';
import {SettlementDetailContract} from '../api/contracts/settlement-detail.contract';
import {SettlementDetailMapper} from '../models/settlement-detail/settlement-detail.mapper';
import {SettlementDetailModel} from '../models/settlement-detail/settlement-detail.model';
import {ConfirmModalComponent} from '../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import settlementsModalConfigurations from './configuration/settlements-main-page-modals-configuration';
import {SettlementFooterActionModel} from '../models/settlement-footer-action.model';
import {SettlementDetailFooterActionsEnum} from '../models/settlement-detail-footer-actions.enum';
import {SettlementDetailLineIdentifierModel} from '../models/settlement-detail/settlement-detail-line-identifier.model';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {SettlementTabsEnum} from './settlement-tabs.enum';
import {SegmentedButtonDisplayModeEnum} from '../../shared/segemented-button/segmented-button-display-mode.enum';
import {SegmentedButtonOptionModel} from '../../shared/segemented-button/segmented-button-option.model';
import {SettlementResultContract} from '../api/contracts/settlement-result.contract';
import {UserService} from '../../shared/services/user.service';
import {Permission} from '../../shared/utils/permission.enum';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import {SettlementsExportExcelApi} from '../../api/ms-settlements/settlements-export-excel.api';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {SettlementReportResultDataMapper} from './models/mappers/settlement-report-result-data/settlement-report-result-data.mapper';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {SettlementReportQueryContract} from '../../api/ms-settlements/settlement-export-excel/contracts/settlement-report-query.contract';
import {SettlementReportResultDetailModel} from '../models/report/settlement-report-result-detail.model';
import {ActivatedRoute} from '@angular/router';
import {BusinessConfigurationEnum} from './models/business-configuration.enum';
import settlementsMainPageConfiguration from './configuration/settlements-main-page.configuration';
import {SettlementMainPageFilterMapper} from './models/mappers/settlement-main-page-filter/settlement-main-page-filter.mapper';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

type WorksheetData = any[][];

@Component({
    selector: 'app-settlements-main-page',
    templateUrl: './settlements-main-page.component.html',
    styleUrls: ['./settlements-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettlementsMainPageComponent
        extends StatefulComponent<SettlementsMainPageStateModel>
        implements OnInit, AfterViewInit, OnDestroy, WithPagination {
    @ViewChild('customerFilterTemplate', {static: true})
    customerTemplateRef: TemplateRef<CodeFilterComponent> = <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('supplierFilterTemplate', {static: true})
    supplierTemplateRef: TemplateRef<CodeFilterComponent> = <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('complaintCodeFilterTemplate', {static: true})
    complaintCodeTemplateRef: TemplateRef<InputAndCodeFilterComponent> = <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('settlementCodeFilterTemplate', {static: true})
    settlementCodeTemplateRef: TemplateRef<InputAndCodeFilterComponent> = <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('statusFilterTemplate', {static: true})
    statusTemplateRef: TemplateRef<CodeFilterComponent> = <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('lastPendingDateFilterTemplate', {static: true})
    lastPendingDateTemplateRef: TemplateRef<DateFilterComponent> = <TemplateRef<DateFilterComponent>>{};
    @ViewChild('lastChangeDatetimeFilterTemplate', {static: true})
    lastChangeDatetimeTemplateRef: TemplateRef<DateFilterComponent> = <TemplateRef<DateFilterComponent>>{};
    @ViewChild('partTotalFilterTemplate', {static: true})
    partTotalTemplateRef: TemplateRef<NumberFilterComponent> = <TemplateRef<NumberFilterComponent>>{};
    @ViewChild('teMinutesFilterTemplate', {static: true})
    teMinutesTemplateRef: TemplateRef<NumberFilterComponent> = <TemplateRef<NumberFilterComponent>>{};
    @ViewChild('settlementGrossAmountFilterTemplate', {static: true})
    settlementGrossAmountTemplateRef: TemplateRef<NumberFilterComponent> = <TemplateRef<NumberFilterComponent>>{};
    @ViewChild('customerFilter')
    customerComponent?: CodeFilterComponent;
    @ViewChild('supplierFilter')
    supplierComponent?: CodeFilterComponent;
    @ViewChild('complaintCodeFilter')
    complaintCodeComponent?: InputAndCodeFilterComponent;
    @ViewChild('settlementCodeFilter')
    settlementCodeComponent?: InputAndCodeFilterComponent;
    @ViewChild('statusFilter')
    statusComponent?: CodeFilterComponent;
    @ViewChild('lastPendingDateFilter')
    lastPendingDateComponent?: DateFilterComponent;
    @ViewChild('lastChangeDatetimeFilter')
    lastChangeDatetimeComponent?: DateFilterComponent;
    @ViewChild('partTotalFilterFilter')
    partTotalComponent?: NumberFilterComponent;
    @ViewChild('teMinutesFilter')
    teMinutesComponent?: NumberFilterComponent;
    @ViewChild('settlementGrossAmountFilter')
    settlementGrossAmountComponent?: NumberFilterComponent;
    customerFilterCodes = new ReplaySubject<CodeFilterModel[]>(1);
    supplierFilterCodes = new ReplaySubject<CodeFilterModel[]>(1);
    statusFilterCodes = new ReplaySubject<CodeFilterModel[]>(1);
    SettlementColumnEnum = SettlementColumnEnum;
    // Event emitter for the scroll events
    public scrollEvent: EventEmitter<void> = new EventEmitter<void>();
    private rejectModalLoadingSubject = new Subject<boolean>();
    private acceptModalLoadingSubject = new Subject<boolean>();
    // Subscriptions variable
    private subscriptions: Subscription = new Subscription();
    // The table data to be injected in the table component
    public tableData: TableModel<SettlementColumnEnum, SettlementSummaryModel, SettlementDetailLineIdentifierModel> = {
        headers: {
            entries: new Map<SettlementColumnEnum, TableHeaderEntryModel<SettlementColumnEnum>>([
                [SettlementColumnEnum.CUSTOMER, settlementsMainPageHeadersConfiguration.customers],
                [SettlementColumnEnum.SUPPLIER, settlementsMainPageHeadersConfiguration.suppliers],
                [SettlementColumnEnum.COMPLAINT_CODE, settlementsMainPageHeadersConfiguration.complaint],
                [SettlementColumnEnum.SETTLEMENT_CODE, settlementsMainPageHeadersConfiguration.settlement],
                [SettlementColumnEnum.STATUS, settlementsMainPageHeadersConfiguration.status],
                [SettlementColumnEnum.LAST_PENDING_DATE, settlementsMainPageHeadersConfiguration.lastPendingDate],
                [SettlementColumnEnum.LAST_CHANGE_DATETIME, settlementsMainPageHeadersConfiguration.lastChangeDatetime],
                [SettlementColumnEnum.MATERIALS_TOTAL_GROSS_COST, settlementsMainPageHeadersConfiguration.materialsTotalGrossCost],
                [SettlementColumnEnum.TE_MINUTES_TOTAL_COST, settlementsMainPageHeadersConfiguration.teMinutesTotalCost],
                [SettlementColumnEnum.SETTLEMENT_TOTAL_COST, settlementsMainPageHeadersConfiguration.settlementTotalCost]
            ])
        },
        expandableLineTooltipDetail: settlementsMainPageConfiguration.fullLineTooltipDetail,
        expandableLines: true
    };
    private readonly TABS_INLINE_STYLE: { [key: string]: string } = {'width': '95px'};
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'SETTLEMENTS_LIST_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    // Excel export
    contextActions: StickyHeaderIconButtonComponent[] = [];
    reportCriteria: SettlementReportQueryContract;
    private readonly EXCEL_DATE_FORMAT: string = 'DD-MM-YYYY';
    private readonly EXCEL_TIME_FORMAT: string = 'HH:mm:ss';
    private worksheetHeader: (string | number)[] = [
        'Supplier code',
        'Supplier description',
        'Complaint number',
        'Settlement number',
        'Part number',
        'Part description',
        'Complaint description',
        'Parts settlement total amount',
        'TE minutes settlement total amount',
        'Settlement total amount',
        'Last pending date',
        'Last pending time',
        'Status ',
        'Last modify date',
        'Last modify time',
        'Parts quantity',
        'Parts net price',
        'Parts total net price',
        'Parts tax rate',
        'Parts total amount',
        'TE minutes quantity',
        'Price per minute',
        'TE minutes additional costs',
        'TE minutes total amount',
        'Blue fapiao number',
        'Delivery note number',
        'Comment'
    ];
    private readonly exportAction: StickyHeaderIconButtonComponent = {
        text: 'Export',
        icon: 'iwp-icon-gen_export_excel',
        click: (): void => {
            this.getSettlementReport({
                searchCriteria: this.search.getSettlementsQuery().searchCriteria,
                sortCriteria: [
                    {
                        criterion: this.search.sortedBy,
                        sortDirection: this.search.sortDirection
                    }
                ]
            });
        },
        id: 'sticky-header-export-excel-button',
        class: 'btn-text-link',
        dataCy: 'sticky-header-export-excel-button',
        tooltip: 'Export to excel'
    };

    constructor(public spinnerService: SpinnerService,
                private settlementsApi: SettlementsApi,
                public search: SettlementSearchCriteriaService,
                private modal: NgbModal,
                private datapickerHelper: DatepickerHelperService,
                private appService: AppService,
                private toast: ToastWrapperService,
                private userService: UserService,
                private settlementsExportExcelApi: SettlementsExportExcelApi,
                private stickyHeaderService: StickyHeaderService,
                private route: ActivatedRoute) {
        super();
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, SettlementsMainPageComponent);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);

        this.setInitialState();
        this.updateDynamicColumnsHeaders();
    }

    /**
     * Returns the correct table title according the business configuration (Customers/Suppliers)
     */
    get tableTitle(): string {
        return this.state.businessConfiguration === BusinessConfigurationEnum.CUSTOMER ?
                settlementsMainPageConfiguration.titleCustomer : settlementsMainPageConfiguration.titleSupplier;
    }

    get initiallySortedBy(): TableSortedByModel<SettlementColumnEnum> {
        return {
            code: SettlementColumnEnum.LAST_CHANGE_DATETIME,
            direction: SortDirection.DESC
        };
    };

    get hasClearAllButton(): boolean {
        return (this.supplierComponent?.isFilterActive ?? false)
                || (this.customerComponent?.isFilterActive ?? false)
                || (this.complaintCodeComponent?.isFilterActive ?? false)
                || (this.settlementCodeComponent?.isFilterActive ?? false)
                || (this.statusComponent?.isFilterActive ?? false)
                || (this.lastPendingDateComponent?.isFilterActive ?? false)
                || (this.lastChangeDatetimeComponent?.isFilterActive ?? false)
                || (this.partTotalComponent?.isFilterActive ?? false)
                || (this.teMinutesComponent?.isFilterActive ?? false)
                || (this.settlementGrossAmountComponent?.isFilterActive ?? false);
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): SettlementDetailModel | null {
        let accordionData: SettlementDetailModel | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    get hasSupplierColumn(): boolean {
        return (this.userService.isMultiSupplier() || this.state.hasAdminPermission)
                && this.state.businessConfiguration === BusinessConfigurationEnum.SUPPLIER;
    }

    get hasCustomerColumn(): boolean {
        return this.state.businessConfiguration === BusinessConfigurationEnum.CUSTOMER;
    }

    ngOnInit(): void {
        this.updateTableFiltersComponents();
        timer(0)
                .subscribe(() => {
                    if (this.userService.hasPermissions([
                        Permission.ADMIN_SETTLEMENT_EXPORT,
                        Permission.SUPPLIER_SETTLEMENT_EXPORT,
                        Permission.FOUR_EYES_SETTLEMENT_EXPORT
                    ], 'any')) {
                        this.contextActions.push(this.exportAction);
                    }
                    this.stickyHeaderService.registerContextActions(this.contextActions);
                });
    }

    ngAfterViewInit(): void {
        this.restoreFilters();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    mapWorksheetData(reportDetail: SettlementReportResultDetailModel[]): WorksheetData {
        let worksheetData: WorksheetData = [];
        this.worksheetHeaderValues();
        if (reportDetail != null) {
            worksheetData = reportDetail?.map((reportDetailData: SettlementReportResultDetailModel) => [
                ...this.getReportDynamicColumn(reportDetailData),
                reportDetailData?.supplier?.code ? reportDetailData.supplier.code : null,
                reportDetailData?.supplier?.name ? reportDetailData.supplier.name : null,
                reportDetailData?.complaintCode ? reportDetailData.complaintCode : null,
                reportDetailData?.settlementCode ? reportDetailData.settlementCode : null,
                reportDetailData?.material?.number ? reportDetailData.material.number : null,
                reportDetailData?.material?.description ? reportDetailData.material.description : null,
                reportDetailData?.complaintDescription ? reportDetailData.complaintDescription : null,
                reportDetailData?.materialsTotalGrossCost ? reportDetailData.materialsTotalGrossCost : 0,
                reportDetailData?.teMinutesTotalCost ? reportDetailData.teMinutesTotalCost : 0,
                reportDetailData?.settlementTotalCost ? reportDetailData.settlementTotalCost : 0,
                reportDetailData?.lastPendingDate ? {
                    v: moment(reportDetailData.lastPendingDate)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                reportDetailData?.lastPendingDate ? {
                    v: moment(reportDetailData.lastPendingDate)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                reportDetailData?.statusName ? reportDetailData.statusName : null,
                reportDetailData?.lastChangeDatetime ? {
                    v: moment(reportDetailData.lastChangeDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                reportDetailData?.lastChangeDatetime ? {
                    v: moment(reportDetailData.lastChangeDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                reportDetailData?.position?.quantity ? reportDetailData.position.quantity : 0,
                reportDetailData?.position?.materialsUnitNetPrice ? reportDetailData.position.materialsUnitNetPrice : 0,
                reportDetailData?.position?.materialsTotalNetCost ? reportDetailData.position.materialsTotalNetCost : 0,
                reportDetailData?.position?.taxRate ? reportDetailData.position.taxRate : 0,
                reportDetailData?.position?.materialsTotalGrossCost ? reportDetailData.position.materialsTotalGrossCost : 0,
                reportDetailData?.position?.teMinutesAmount ? reportDetailData.position.teMinutesAmount : 0,
                reportDetailData?.position?.teMinutesUnitCost ? reportDetailData.position.teMinutesUnitCost : 0,
                reportDetailData?.position?.teMinutesAdditionalCost ? reportDetailData.position.teMinutesAdditionalCost : 0,
                reportDetailData?.position?.teMinutesTotalCost ? reportDetailData.position.teMinutesTotalCost : 0,
                reportDetailData?.position?.blueFapiaoNumber ? reportDetailData.position.blueFapiaoNumber : null,
                reportDetailData?.position?.deliveryNumber ? reportDetailData.position.deliveryNumber : null,
                reportDetailData?.position?.comments ? reportDetailData.position.comments : null
            ]);
        }
        const worksheet = [this.worksheetHeader].concat(worksheetData);
        return worksheet;
    }

    worksheetHeaderValues(): void {
        if (this.hasCustomerColumn) {
            this.worksheetHeader = ['Customer code', 'Customer description', ...this.worksheetHeader];
        }
    }

    exportExcel(positions: SettlementReportResultDetailModel[]): void {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(this.mapWorksheetData(positions), {skipHeader: true});
        const excelExportFileName = this.hasCustomerColumn ? settlementsMainPageConfiguration.exportFileNameCustomer
                : settlementsMainPageConfiguration.exportFileNameSupplier;
        const excelWorksheetTitle = this.hasCustomerColumn ? settlementsMainPageConfiguration.exportWorksheetTitleCustomer
                : settlementsMainPageConfiguration.exportWorksheetTitleSupplier;
        // Format the date/time columns, so they don't show up as ######
        worksheet['!cols'] = new Array(this.worksheetHeaderValues.length);
        worksheet['!cols'][10] = {wch: 10.5};
        worksheet['!cols'][12] = {wch: 8.5};

        XLSX.utils.book_append_sheet(workbook, worksheet, excelWorksheetTitle);

        // > The default behavior for all parsers is to generate number cells.
        // > Setting cellDates to true will force the generators to store dates.
        // https://docs.sheetjs.com/#user-content-dates
        XLSX.writeFile(workbook, excelExportFileName, {cellDates: true});
    }

    getSupplierFilterCodes(): void {
        this.subscriptions.add(this.settlementsApi.getAllSettlementSupplierBase()
                .subscribe((suppliers) => {
                    this.supplierFilterCodes.next(SettlementMainPageFilterMapper.fromSupplierBaseContractToCodeFilterModel(suppliers));
                }));
    }

    getCustomerFilterCodes(): void {
        this.subscriptions.add(this.settlementsApi.getAllSettlementCustomerBase()
                .subscribe((customers) => {
                    this.customerFilterCodes.next(SettlementMainPageFilterMapper.fromCustomerBaseContractToCodeFilterModel(customers));
                }));
    }

    getStatusFilterCodes(): void {
        this.subscriptions.add(this.settlementsApi.getAllSettlementStatusBase()
                .subscribe((statuses) => {
                    this.statusFilterCodes.next(SettlementMainPageFilterMapper.fromSettlementDataModelToCodeFilterModel(statuses));
                }));
    }

    restoreFilters(): void {
        this.customerComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.CUSTOMER) as CodeFilterStateModel);
        this.supplierComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.SUPPLIER) as CodeFilterStateModel);
        this.complaintCodeComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.COMPLAINT_CODE) as InputAndCodeFilterStateModel);
        this.settlementCodeComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.SETTLEMENT_CODE) as InputAndCodeFilterStateModel);
        this.statusComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.STATUS) as CodeFilterStateModel);
        this.lastPendingDateComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.LAST_PENDING_DATE) as DateFilterStateModel);
        this.lastChangeDatetimeComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.LAST_CHANGE_DATETIME) as DateFilterStateModel);
        this.partTotalComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.MATERIALS_TOTAL_GROSS_COST) as NumberFilterStateModel);
        this.teMinutesComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.TE_MINUTES_TOTAL_COST) as NumberFilterStateModel);
        this.settlementGrossAmountComponent?.initFilter(
                this.search.stateMap.get(SettlementColumnEnum.SETTLEMENT_TOTAL_COST) as NumberFilterStateModel);
    }

    sortedByChange(sortedBy: TableSortedByModel<SettlementColumnEnum>): void {
        this.search.sortedBy = sortedBy.code;
        this.search.sortDirection = sortedBy.direction;
        this.retrieveTableData(true);
    }

    changePage = (pageNumber: number): void => {
        this.search.pageNumber = pageNumber;
        this.retrieveTableData(true);
    };

    hasPagination(): boolean {
        return this.search.totalResults > this.search.pageSize;
    }

    filterChange(state: AbstractFilterStateModel): void {
        // To prevent this casting to the column enum, the AbstractFilterStateModel should be generic. Check the interface for more info.
        this.search.setState(state.filterName as SettlementColumnEnum, state, () => {
            this.retrieveTableData();
        });
    }

    clearAllFilters(): void {
        this.customerComponent?.resetFilter();
        this.supplierComponent?.resetFilter();
        this.complaintCodeComponent?.resetFilter();
        this.settlementCodeComponent?.resetFilter();
        this.statusComponent?.resetFilter();
        this.lastPendingDateComponent?.resetFilter();
        this.lastChangeDatetimeComponent?.resetFilter();
        this.partTotalComponent?.resetFilter();
        this.teMinutesComponent?.resetFilter();
        this.settlementGrossAmountComponent?.resetFilter();

        this.search.stateMap.clear();

        this.changePage(1);

    }

    loadDetailData(line: TableLineModel<SettlementColumnEnum, SettlementDetailModel, SettlementDetailLineIdentifierModel>): void {
        if (line != null && line.accordionData == null) {

            const complaintCode: string | null = line.identifier?.complaintCode ?? null;
            const settlementCode: string | null = line.identifier?.settlementCode ?? null;

            this.subscriptions.add(
                    this.settlementsApi.getSettlement(this.getParams(complaintCode, settlementCode), this.state.businessConfiguration)
                            .subscribe((detailData: SettlementDetailContract) => {
                                line.accordionData = SettlementDetailMapper.fromSettlementChangeDetailContract(detailData);
                                const lineSummary = this.getSettlementSummaryFromDetail(line.accordionData);
                                line.data = this.mapLineData(lineSummary);

                                this.appService.onRouterOutletContainerScroll
                                        .subscribe(() => {
                                            this.scrollEvent.emit();
                                        });
                            }));
        }
        this.storeState({selectedLine: line});
    }

    triggerFooterAction(action: SettlementFooterActionModel): void {
        switch (action.type) {
            case SettlementDetailFooterActionsEnum.ACCEPT:
                this.acceptSettlementModal(action.settlementData.complaintCode, action.settlementData.settlementCode);
                break;
            case SettlementDetailFooterActionsEnum.REJECT:
                this.rejectSettlementModal(action.settlementData.complaintCode, action.settlementData.settlementCode);
                break;
            default:
                return;
        }
    }

    changeListTab(newTab: SettlementTabsEnum): void {
        if (newTab !== this.state.selectedTab) {
            this.storeState({selectedTab: newTab});
            this.clearAllFilters();
        } else {
            this.retrieveTableData();
        }
    }

    private setInitialState(): void {
        this.subscriptions.add(
                this.route.url.subscribe(() => {
                    this.storeState({businessConfiguration: this.route.snapshot?.data['businessConfiguration']});
                }));

        this.storeState({
            hasAdminPermission: this.userService.hasPermissions([Permission.ADMIN_SETTLEMENT_VIEW]),
            hasSupplierPermission: this.userService.hasPermissions([
                Permission.SUPPLIER_SETTLEMENT_VIEW,
                Permission.SUPPLIER_SETTLEMENT_ACCEPT,
                Permission.SUPPLIER_SETTLEMENT_REJECT
            ]),
            hasCustomerPermission: this.userService.hasPermissions([
                Permission.FOUR_EYES_SETTLEMENT_VIEW,
                Permission.FOUR_EYES_SETTLEMENT_ACCEPT,
                Permission.FOUR_EYES_SETTLEMENT_REJECT
            ])
        });
        this.storeState({listTypeOptions: this.getListTabOptions()});
    }

    private getListTabOptions(): SegmentedButtonOptionModel<SettlementTabsEnum>[] {
        const allOption: SegmentedButtonOptionModel<SettlementTabsEnum> = {
            label: 'ALL',
            value: SettlementTabsEnum.ALL,
            isSelected: true,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            inlineStyle: this.TABS_INLINE_STYLE
        };

        const todoOption: SegmentedButtonOptionModel<SettlementTabsEnum> = {
            label: 'TODO',
            value: SettlementTabsEnum.TODO,
            isSelected: false,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            inlineStyle: this.TABS_INLINE_STYLE
        };

        let options: SegmentedButtonOptionModel<SettlementTabsEnum>[] = [];
        if (this.state.hasAdminPermission) {
            options = [];
        } else if (this.state.hasSupplierPermission || this.state.hasCustomerPermission) {
            options = [allOption, todoOption];
        }
        return options;
    }

    private setTodoTabCount(todoCount: number): void {
        this.storeState({
            listTypeOptions: this.state.listTypeOptions.map((option) =>
                    option.value === SettlementTabsEnum.TODO ? {...option, label: `(${todoCount}) TODO`} : option)
        });
    }

    private getSettlementSummaryFromDetail(lineData: SettlementDetailModel): SettlementSummaryModel {
        return {
            currency: lineData.currency,
            supplier: lineData?.supplier,
            customer: lineData?.customer,
            complaintCode: lineData.complaintCode,
            settlementCode: lineData.settlementCode,
            status: lineData.status,
            lastPendingDate: lineData.lastPendingDate,
            lastChangeDatetime: lineData.lastChangeDatetime,
            materialsTotalGrossCost: lineData.materialsTotalGrossCost,
            teMinutesTotalCost: lineData.teMinutesTotalCost,
            settlementTotalCost: lineData.settlementTotalCost,
            isTodo: lineData.isTodo
        };
    }

    private updateTableFiltersComponents(): void {
        if (this.hasSupplierColumn && !this.hasCustomerColumn) {
            this.tableData.headers.entries.get(SettlementColumnEnum.SUPPLIER).filterType = this.supplierTemplateRef;
        }
        if (this.hasCustomerColumn) {
            this.tableData.headers.entries.get(SettlementColumnEnum.CUSTOMER).filterType = this.customerTemplateRef;
        }
        this.tableData.headers.entries.get(SettlementColumnEnum.COMPLAINT_CODE).filterType = this.complaintCodeTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.SETTLEMENT_CODE).filterType = this.settlementCodeTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.STATUS).filterType = this.statusTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.LAST_PENDING_DATE).filterType = this.lastPendingDateTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.LAST_CHANGE_DATETIME).filterType = this.lastChangeDatetimeTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.MATERIALS_TOTAL_GROSS_COST).filterType = this.partTotalTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.TE_MINUTES_TOTAL_COST).filterType = this.teMinutesTemplateRef;
        this.tableData.headers.entries.get(SettlementColumnEnum.SETTLEMENT_TOTAL_COST)
                .filterType = this.settlementGrossAmountTemplateRef;
    }

    private updateDynamicColumnsHeaders(): void {
        if (!this.hasSupplierColumn && !this.hasCustomerColumn) {
            this.tableData.headers.entries.delete(SettlementColumnEnum.CUSTOMER);
            this.tableData.headers.entries.delete(SettlementColumnEnum.SUPPLIER);
        }
        if (this.hasSupplierColumn) {
            this.tableData.headers.entries.set(SettlementColumnEnum.SUPPLIER, settlementsMainPageHeadersConfiguration.suppliers);
            this.tableData.headers.entries.delete(SettlementColumnEnum.CUSTOMER);
        }
        if (this.hasCustomerColumn) {
            this.tableData.headers.entries.set(SettlementColumnEnum.CUSTOMER, settlementsMainPageHeadersConfiguration.customers);
            this.tableData.headers.entries.delete(SettlementColumnEnum.SUPPLIER);
        }
    }

    /**
     * Retrieves the table data from the service
     */
    private retrieveTableData(hasPaginationCriteria?: boolean): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        const query: SettlementQueryContract = this.search.getSettlementsQuery(hasPaginationCriteria);
        const searchService = this.getCurrentTabSearchService();
        const todoCountApi: Observable<number> = this.state.hasAdminPermission ?
                of(undefined) : this.settlementsApi.getSettlementsToDoCount(this.state.businessConfiguration);

        this.subscriptions.add(
                forkJoin([searchService(query, this.state.businessConfiguration), todoCountApi]
                )
                        .pipe(finalize(() => {
                            this.storeState({isAnimated: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, SettlementsMainPageComponent);
                            this.storeState({isLoading: false});
                        }))
                        .subscribe(([settlements, todoCount]) => {
                                this.search.totalResults = settlements.totalResults;
                                this.tableData.staticSection = this.mapData(settlements.settlementsPage);
                                const line = this.getLineToAnimate();
                                this.updateLineAnimation(line, this.state.isAnimated);
                                if (this.state.isAnimated) {
                                    timer(settlementsMainPageConfiguration.animationTimeout)
                                            .subscribe(() => {
                                                this.storeState({isAnimated: false});
                                                this.updateLineAnimation(line, this.state.isAnimated);
                                            });
                                }
                                this.setTodoTabCount(todoCount);
                        })
        );
    }

    private getLineToAnimate(): TableLineModel<SettlementColumnEnum, SettlementSummaryModel, SettlementDetailLineIdentifierModel> {
        return this.tableData.staticSection.find((findLine?) =>
                this.state?.identifier?.complaintCode === findLine?.identifier?.complaintCode
                && this.state?.identifier?.settlementCode === findLine?.identifier?.settlementCode);
    }

    private updateLineAnimation(line: TableLineModel<SettlementColumnEnum, SettlementSummaryModel, SettlementDetailLineIdentifierModel>,
                                isAnimated: boolean): void {
        if (line != null) {
            line.isAnimated = isAnimated;
        }
    }

    /**
     * Populates the staticData array with the current {@link SettlementSummaryModel}
     * @param settlementTableData the {@link SettlementSummaryModel} from which to update the array
     */
    private mapData(settlementTableData: readonly SettlementSummaryModel[]):
            TableLineModel<SettlementColumnEnum, SettlementSummaryModel, SettlementDetailLineIdentifierModel>[] {
        this.updateDynamicColumnsHeaders();
        this.updateTableFiltersComponents();

        let lines: TableLineModel<SettlementColumnEnum, SettlementSummaryModel, SettlementDetailLineIdentifierModel>[] = [];

        if (settlementTableData != null) {
            lines = settlementTableData.map((settlement) => ({
                version: null,
                isOpen: false,
                data: this.mapLineData(settlement),
                borderColorIdentification: this.getBorderColorIdentification(settlement),
                identifier: {
                    complaintCode: settlement.complaintCode,
                    settlementCode: settlement.settlementCode
                }
            }));
        }
        return lines;
    }

    private getBorderColorIdentification(data: SettlementSummaryModel): string {
        return data?.isTodo ? `settlement-${data?.status?.code ?? ''}` : null;
    }

    private retrieveMapLineDynamicValues(data: SettlementSummaryModel): Map<SettlementColumnEnum, TableLineDataModel> {
        const dynamicValues: Map<SettlementColumnEnum, TableLineDataModel> = new Map<SettlementColumnEnum, TableLineDataModel>([]);
        if (this.hasCustomerColumn) {
            dynamicValues.set(SettlementColumnEnum.CUSTOMER, {cellValue: data?.customer});
        }
        if (this.hasSupplierColumn) {
            dynamicValues.set(SettlementColumnEnum.SUPPLIER, {cellValue: data?.supplier});
        }
        return dynamicValues;
    }

    /**
     * Maps a {@link SettlementSummaryModel} to a Map<SettlementColumnEnum, TableLineDataModel>
     */
    private mapLineData(data: SettlementSummaryModel): Map<SettlementColumnEnum, TableLineDataModel> {
        const map: Map<SettlementColumnEnum, TableLineDataModel> = new Map<SettlementColumnEnum, TableLineDataModel>([
            ...this.retrieveMapLineDynamicValues(data),
            [
                SettlementColumnEnum.COMPLAINT_CODE, {
                cellValue: data?.complaintCode
            }
            ],
            [
                SettlementColumnEnum.SETTLEMENT_CODE, {
                cellValue: data?.settlementCode
            }
            ],
            [
                SettlementColumnEnum.STATUS, {
                cellValue: data?.status,
                dotClass: `settlement-status ${data?.status.code}`
            }
            ],
            [
                SettlementColumnEnum.LAST_PENDING_DATE, {
                cellValue: DatepickerHelperService.convertTimestampToDateAndTime(data?.lastPendingDate)
            }
            ],
            [
                SettlementColumnEnum.LAST_CHANGE_DATETIME, {
                cellValue: DatepickerHelperService.convertTimestampToDateAndTime(data?.lastChangeDatetime)
            }
            ],
            [
                SettlementColumnEnum.MATERIALS_TOTAL_GROSS_COST, {
                cellValue: {
                    data: data?.materialsTotalGrossCost,
                    currency: data?.currency
                }
            }
            ],
            [
                SettlementColumnEnum.TE_MINUTES_TOTAL_COST, {
                cellValue: {
                    data: data?.teMinutesTotalCost,
                    currency: data?.currency
                }
            }
            ],
            [
                SettlementColumnEnum.SETTLEMENT_TOTAL_COST, {
                cellValue: {
                    data: data?.settlementTotalCost,
                    currency: data?.currency
                }
            }
            ]
        ]);
        this.removeHiddenColumnsValues(map);

        return map;
    }

    private removeHiddenColumnsValues(map: Map<SettlementColumnEnum, TableLineDataModel>): void {
        if (!this.hasSupplierColumn) {
            map.delete(SettlementColumnEnum.SUPPLIER);
        } else {
            map.delete(SettlementColumnEnum.CUSTOMER);
        }
    }

    /**
     * Call the reject Settlement endpoint
     * @param complaintCode the code of the Settlement to reject
     * @param settlementCode the code of the Settlement to reject
     * @param motivation the justification por the Settlement rejection
     */
    private async rejectSettlement(complaintCode: string, settlementCode: string, motivation: string): Promise<boolean> {
        let success = false;
        await this.settlementsApi.rejectSettlement(this.getParams(complaintCode, settlementCode),
                {motivationToReject: toBase64Utf16LittleEndian(motivation)},
                this.state.businessConfiguration)
                .pipe(take(1))
                .toPromise()
                .then(() => {
                    success = true;
                    const complaint = complaintCode ?? '-';
                    const settlement = settlementCode ?? '-';
                    this.toast.success(`The financial complaint settlement ${complaint}/${settlement} was successfully rejected.`,
                            `Success`);
                    this.storeState({isAnimated: true, identifier: {complaintCode, settlementCode}});
                    this.retrieveTableData(true);
                })
                .finally(() => {
                    this.rejectModalLoadingSubject.next(false);
                });
        return success;
    }

    /**
     * Call the accept Settlement endpoint
     * @param complaintCode the code of the Settlement to accept
     * @param settlementCode the code of the Settlement to accept
     * @param comment the justification por the Settlement acception
     */
    private async acceptSettlement(complaintCode: string, settlementCode: string, comment: string): Promise<boolean> {
        let success = false;
        await this.settlementsApi.acceptSettlement(this.getParams(complaintCode, settlementCode),
                {acceptanceComment: toBase64Utf16LittleEndian(comment)},
                this.state.businessConfiguration)
                .pipe(take(1))
                .toPromise()
                .then(() => {
                    success = true;
                    const complaint = complaintCode ?? '-';
                    const settlement = settlementCode ?? '-';
                    this.toast.success(`The financial complaint settlement ${complaint}/${settlement} was successfully accepted!`,
                            `Success`);
                    this.storeState({isAnimated: true, identifier: {complaintCode, settlementCode}});
                    this.retrieveTableData(true);
                })
                .finally(() => {
                    this.acceptModalLoadingSubject.next(false);
                });
        return success;
    }

    /**
     * Opens the reject Settlement motivation modal
     * @param complaintCode the code of the Complaint to reject
     * @param settlementCode the code of the Settlement to reject
     */
    private rejectSettlementModal(complaintCode: string, settlementCode: string): void {
        let isPrimaryAction = false;
        let settlementNotes: string;

        const rejectModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            beforeDismiss: () => {
                if (isPrimaryAction) {
                    isPrimaryAction = false;
                    return this.rejectSettlement(complaintCode, settlementCode, settlementNotes);
                } else {
                    return true;
                }
            }
        }).componentInstance;

        if (this.hasCustomerColumn) {
            rejectModalConfiguration.modalConfiguration = {
                ...settlementsModalConfigurations.rejectCustomer,
                isLoading: this.acceptModalLoadingSubject
            };
        } else {
            rejectModalConfiguration.modalConfiguration = {
                ...settlementsModalConfigurations.rejectSupplier,
                isLoading: this.acceptModalLoadingSubject
            };
        }

        rejectModalConfiguration.primaryEvent.subscribe((notes: string) => {
            this.rejectModalLoadingSubject.next(true);
            isPrimaryAction = true;
            settlementNotes = notes;
        });
    }

    /**
     * Opens the accept Settlement motivation modal
     * @param complaintCode the code of the Complaint to accept
     * @param settlementCode the code of the Settlement to accept
     */
    private acceptSettlementModal(complaintCode: string, settlementCode: string): void {
        let isPrimaryAction = false;
        let settlementNotes: string;

        const acceptModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            beforeDismiss: () => {
                if (isPrimaryAction) {
                    isPrimaryAction = false;
                    return this.acceptSettlement(complaintCode, settlementCode, settlementNotes);
                } else {
                    return true;
                }
            }
        }).componentInstance;

        if (this.hasCustomerColumn) {
            acceptModalConfiguration.modalConfiguration = {
                ...settlementsModalConfigurations.acceptCustomer,
                isLoading: this.acceptModalLoadingSubject
            };
        } else {
            acceptModalConfiguration.modalConfiguration = {
                ...settlementsModalConfigurations.acceptSupplier,
                isLoading: this.acceptModalLoadingSubject
            };
        }

        acceptModalConfiguration.primaryEvent.subscribe((notes: string) => {
            this.acceptModalLoadingSubject.next(true);
            isPrimaryAction = true;
            settlementNotes = notes;
        });

    }

    private getCurrentTabSearchService(): (settlementsQuery: SettlementQueryContract, businessConfiguration: string)
            => Observable<SettlementResultContract> {
        if (this.state.hasAdminPermission) {
            return this.settlementsApi.getSettlements;
        } else {
            switch (this.state.selectedTab) {
                case SettlementTabsEnum.ALL:
                    return this.settlementsApi.getSettlements;
                case SettlementTabsEnum.TODO:
                    return this.settlementsApi.getSettlementsToDo;
                default:
                    return this.settlementsApi.getSettlements;
            }
        }
    }

    /**
     * Gets the settlement report response from the BE
     * @param queryModel the search and sort criteria for the query
     * Calls {@link exportExcel}
     * Opens warning modal if exceed-records-number error is returned
     */
    private getSettlementReport(queryModel: SettlementReportQueryContract): void {
        this.exportAction.loading = this.exportAction.disabled = true;
        this.stickyHeaderService.registerContextActions(this.contextActions);
        switch (this.state.selectedTab) {
            case SettlementTabsEnum.ALL:
                this.settlementsExportExcelApi.getSettlementsReport(queryModel, this.state.businessConfiguration)
                        .subscribe((value) => {
                            this.exportExcel(SettlementReportResultDataMapper
                                    .fromSettlementReportDetailContractToSettlementReportResultDetailModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
                break;
            case SettlementTabsEnum.TODO:
                this.settlementsExportExcelApi.getSettlementsTodoReport(queryModel, this.state.businessConfiguration)
                        .subscribe((value) => {
                            this.exportExcel(SettlementReportResultDataMapper
                                    .fromSettlementReportDetailContractToSettlementReportResultDetailModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
                break;
            default:
                this.settlementsExportExcelApi.getSettlementsReport(queryModel, this.state.businessConfiguration)
                        .subscribe((value) => {
                            this.exportExcel(SettlementReportResultDataMapper
                                    .fromSettlementReportDetailContractToSettlementReportResultDetailModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
        }
    }

    private reportErrorModal(errorMessage: string): void {
        if (errorMessage != null) {
            const confirmModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;
            confirmModalInstance.modalConfiguration = {
                title: 'Warning',
                alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                body: {
                    title: errorMessage
                },
                secondaryActionTitle: 'Close'
            };
        }
    }

    private actionReportResult(): void {
        this.exportAction.loading = this.exportAction.disabled = false;
        this.stickyHeaderService.registerContextActions(this.contextActions);
    }

    private getReportDynamicColumn(reportDetailData: SettlementReportResultDetailModel): string[] {
        let reportColumns: string[] = [];
        if (this.hasCustomerColumn) {
            reportColumns = [
                reportDetailData?.customer?.code ? reportDetailData.customer.code : null,
                reportDetailData?.customer?.name ? reportDetailData.customer.name : null
            ];
        }
        return reportColumns;
    }

    /**
     * Returns the parameters from complaint and settlement code
     * @param complaintCode the complaint code.
     * @param settlementCode the settlement code.
     */
    private getParams(complaintCode: string, settlementCode: string): HttpParams {
        let params = new HttpParams();
        if (complaintCode != null) {
            params = params.append('complaintCode', complaintCode);
        }
        if (settlementCode != null) {
            params = params.append('settlementCode', settlementCode);
        }
        return params;
    }
}
